import React, { useMemo, useCallback } from 'react';
import { useTypedSelector } from '../../types/global';
import applicationListSelectors from '../../modules/application/list/applicationListSelectors';
import { selectEnvironment } from '../../modules/global';
import { AVS_ENABLED, IBAN_VERIFICATION, ENVIRONMENT_SANDBOX } from '../../utils/constants';
import PromotionBannerContainer, {
  PromotionBannerItem,
} from './PromotionBannerContainer';
import AvsPromotionBannerContent from './AvsPromotionBannerContent';
import InternationalAvsPromotionBannerContent from './InternationalAvsPromotionBannerContent';
import { useAvsPromotionBannerHandler } from './AvsPromotionBannerHandler';
import { useInternationalAvsPromotionBannerHandler } from './InternationalAvsPromotionBannerHandler';
import { accountVerificationStorage } from '../../view/AccountVerificationPage/accountVerificationStorage';
import { internationalAvsStorage } from './internationalAvsStorage';

const shouldShowAvsOverlay = (application: any) => {
  if (!application) return false;

  const hasAvsEnabledTrait =
    application?.traits.find((trait: string) => trait === AVS_ENABLED) ?? false;
  const NoIbanVerificationLimit = !application?.limits?.find(
    (limit: any) => limit.limit_type === IBAN_VERIFICATION,
  );

  return (
    application.plan.name !== 'SANDBOX' &&
    !(NoIbanVerificationLimit && hasAvsEnabledTrait)
  );
};

function PromotionBannerWrapper() {
  const environment = useTypedSelector(selectEnvironment);
  const application = useTypedSelector(
    applicationListSelectors.selectApplication,
  );

  const avsHandler = useAvsPromotionBannerHandler();
  const internationalAvsHandler = useInternationalAvsPromotionBannerHandler();

  const shouldShowAvs =
    application && shouldShowAvsOverlay(application) && avsHandler.isVisible;

  const shouldShowInternationalAvs =
    application?.id && 
    internationalAvsHandler.isVisible &&
    environment !== ENVIRONMENT_SANDBOX &&
    internationalAvsStorage.shouldShowBanner(environment, application.id);

  const bannerItems: PromotionBannerItem[] = useMemo(() => {
    const items: PromotionBannerItem[] = [];

    if (shouldShowInternationalAvs) {
      items.push({
        id: 'international-avs',
        component: InternationalAvsPromotionBannerContent,
        props: {
          onTryNow: internationalAvsHandler.handleTryNow,
          onReadDocumentation: internationalAvsHandler.handleReadDocumentation,
          isRequestButtonDisabled: internationalAvsHandler.isRequestButtonDisabled,
        },
        duration: 20000,
      });
    }

    if (shouldShowAvs) {
      items.push({
        id: 'avs',
        component: AvsPromotionBannerContent,
        props: {
          onTryNow: avsHandler.handleTryNow,
          onReadDocumentation: avsHandler.handleReadDocumentation,
        },
        duration: 15000,
      });
    }

    return items;
  }, [
    shouldShowInternationalAvs,
    shouldShowAvs,
    internationalAvsHandler.handleTryNow,
    internationalAvsHandler.handleReadDocumentation,
    internationalAvsHandler.isRequestButtonDisabled,
    avsHandler.handleTryNow,
    avsHandler.handleReadDocumentation,
  ]);

  const handleClose = useCallback(() => {
    if (application?.id) {
      if (shouldShowInternationalAvs) {
        internationalAvsStorage.setBannerDismissed(environment, application.id);
        internationalAvsHandler.setIsVisible(false);
      }
      if (shouldShowAvs) {
        accountVerificationStorage.dismissBanner(environment, application.id);
        avsHandler.setIsVisible(false);
      }
    }
  }, [application?.id, shouldShowInternationalAvs, shouldShowAvs, environment, internationalAvsHandler, avsHandler]);

  if (bannerItems.length === 0) {
    return null;
  }

  return (
    <PromotionBannerContainer banners={bannerItems} onClose={handleClose} />
  );
}

export default PromotionBannerWrapper;